import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, X } from 'lucide-react';

interface SearchableSelectOption {
  value: string;
  label: string;
  subtitle?: string;
  icon?: React.ReactNode;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  icon?: React.ReactNode;
  searchPlaceholder?: string;
  className?: string;
  disabled?: boolean;
  allowClear?: boolean;
  getDisplayValue?: (value: string) => string;
  showAllOnOpen?: boolean; // Si true, affiche toutes les options au lieu de 3
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Sélectionner...',
  icon,
  searchPlaceholder = 'Rechercher...',
  className = '',
  disabled = false,
  allowClear = false,
  getDisplayValue,
  showAllOnOpen = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [inputValue, setInputValue] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedOption = options.find(opt => opt.value === value);
  const displayValue = selectedOption 
    ? selectedOption.label 
    : (getDisplayValue ? getDisplayValue(value) : value || '');

  // Synchroniser inputValue avec la valeur sélectionnée
  useEffect(() => {
    if (!isOpen) {
      setInputValue(value ? displayValue : '');
    }
  }, [value, displayValue, isOpen]);

  // Fermer quand on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
        // Restaurer la valeur affichée si on n'a pas sélectionné
        if (value) {
          setInputValue(displayValue);
        } else {
          setInputValue('');
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [value, displayValue]);

  // Focus sur l'input quand on ouvre
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      // Sélectionner tout le texte si une valeur est déjà sélectionnée
      if (value) {
        inputRef.current.select();
      }
    }
  }, [isOpen, value]);

  // Filtrer les options selon la recherche
  const filteredOptions = searchQuery.length > 0
    ? options.filter(opt =>
        opt.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (opt.subtitle && opt.subtitle.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : options;

  // Afficher les options par défaut (3 ou toutes selon showAllOnOpen)
  const defaultSuggestions = showAllOnOpen 
    ? filteredOptions 
    : filteredOptions.slice(0, 3);
  const showSuggestions = isOpen && (searchQuery.length === 0 ? defaultSuggestions : filteredOptions);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    // Filtrer seulement si on a du texte, sinon afficher les 3 premières
    setSearchQuery(newValue);
    
    // Si le champ est vide, on réinitialise la sélection
    if (newValue === '') {
      onChange('');
    }
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    // Quand on focus, on affiche les 3 premières options (pas de filtre)
    setSearchQuery('');
    // Sélectionner tout le texte pour faciliter la recherche
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.select();
      }
    }, 0);
  };

  const handleInputClick = () => {
    setIsOpen(true);
    // Quand on clique, on affiche les 3 premières options (pas de filtre)
    setSearchQuery('');
    // Sélectionner tout le texte pour faciliter la recherche
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.select();
      }
    }, 0);
  };

  const handleSelect = (optionValue: string) => {
    const selected = options.find(opt => opt.value === optionValue);
    onChange(optionValue);
    setInputValue(selected ? selected.label : '');
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setInputValue('');
    setSearchQuery('');
    setIsOpen(false);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      {/* Input principal éditable */}
      <div className="relative">
        {icon && (
          <span className="absolute left-3 top-2.5 text-slate-400 flex-shrink-0 pointer-events-none z-10">
            {icon}
          </span>
        )}
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onClick={handleInputClick}
          placeholder={placeholder}
          disabled={disabled}
          className={`
            w-full ${icon ? 'pl-9' : 'pl-3'} pr-10 p-2.5 border border-slate-300 rounded-lg 
            focus:ring-2 focus:ring-amber-500 focus:border-amber-500 
            outline-none bg-white text-slate-900
            disabled:bg-slate-50 disabled:cursor-not-allowed
            ${isOpen ? 'ring-2 ring-amber-500 border-amber-500' : ''}
            transition-all
            ${className}
          `}
        />
        <div className="absolute right-3 top-2.5 flex items-center gap-2 pointer-events-none">
          {allowClear && value && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="text-slate-400 hover:text-slate-600 p-0.5 rounded pointer-events-auto"
              tabIndex={-1}
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {/* Dropdown avec suggestions */}
      {isOpen && showSuggestions && showSuggestions.length > 0 && (
        <div className="absolute z-50 w-full bg-white border border-slate-200 rounded-xl mt-2 shadow-xl max-h-64 overflow-hidden animate-in fade-in zoom-in-95 duration-100">
          {/* Liste des options */}
          <div className="max-h-64 overflow-y-auto">
            {showSuggestions.map((option) => (
              <div
                key={option.value}
                onClick={() => handleSelect(option.value)}
                className={`
                  p-3 hover:bg-slate-50 cursor-pointer border-b border-slate-50 last:border-0 
                  flex items-center transition-colors
                  ${value === option.value ? 'bg-amber-50' : ''}
                `}
              >
                {option.icon && (
                  <span className="mr-3 text-slate-400 flex-shrink-0">{option.icon}</span>
                )}
                <div className="flex-1 min-w-0">
                  <p className={`font-medium text-sm ${value === option.value ? 'text-amber-700' : 'text-slate-800'}`}>
                    {option.label}
                  </p>
                  {option.subtitle && (
                    <p className="text-xs text-slate-500 mt-0.5">{option.subtitle}</p>
                  )}
                </div>
                {value === option.value && (
                  <div className="ml-2 w-2 h-2 bg-amber-500 rounded-full flex-shrink-0"></div>
                )}
              </div>
            ))}
            {searchQuery.length > 0 && filteredOptions.length === 0 && (
              <div className="p-4 text-sm text-slate-400 text-center">
                Aucun résultat trouvé.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};




